import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import profileApi from '../../../app/api/profileApi';

const getChangeTypeColor = (changeType) => {
  switch (changeType) {
    case 0: // Created
    case 'Created':
      return 'success';
    case 1: // Updated
    case 'Updated':
      return 'info';
    case 2: // Deleted
    case 'Deleted':
      return 'error';
    case 3: // StatusChanged
    case 'StatusChanged':
      return 'warning';
    case 4: // DraftCreated
    case 'DraftCreated':
      return 'success';
    case 5: // DraftEdited
    case 'DraftEdited':
      return 'secondary';
    default:
      return 'default';
  }
};

const getChangeTypeLabel = (changeType) => {
  switch (changeType) {
    case 0: return 'Created';
    case 1: return 'Updated';
    case 2: return 'Deleted';
    case 3: return 'StatusChanged';
    case 4: return 'Draft Created';
    case 5: return 'Draft Edited';
    case 'DraftCreated': return 'Draft Created';
    case 'DraftEdited': return 'Draft Edited';
    default: return changeType;
  }
};

const getSourceColor = (source) => {
  switch (source) {
    case 'User':
      return 'primary';
    case 'Skillko':
      return 'secondary';
    case 'SitePass':
      return 'secondary';
    case 'Import':
      return 'warning';
    case 'System':
      return 'default';
    default:
      return 'default';
  }
};

const formatFieldName = (fieldName) => {
  if (!fieldName) return '-';
  // Convert camelCase/PascalCase to readable format
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

const parseChanges = (changesJson) => {
  if (!changesJson) return [];
  try {
    const parsed = JSON.parse(changesJson);
    return Object.entries(parsed).map(([field, value]) => ({
      field,
      value
    }));
  } catch {
    return [];
  }
};

const AuditRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  const changes = useMemo(() => parseChanges(row.changes), [row.changes]);
  const hasMultipleChanges = changes.length > 1;

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: hasMultipleChanges ? 'unset' : undefined } }}>
        <TableCell>
          {hasMultipleChanges && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <Icon icon="mdi:keyboard-arrow-up" /> : <Icon icon="mdi:keyboard-arrow-down" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{dayjs(row.changedAt).format('DD/MM/YYYY HH:mm:ss')}</TableCell>
        <TableCell>
          <Chip 
            label={getChangeTypeLabel(row.changeType)} 
            color={getChangeTypeColor(row.changeType)} 
            size="small"
          />
        </TableCell>
        <TableCell>{formatFieldName(row.section)}</TableCell>
        <TableCell>
          {changes.length === 1 ? (
            <Typography variant="body2">
              <span style={{ fontWeight: 500 }}>{formatFieldName(changes[0].field)}</span>
              {' → '}
              <span style={{ color: '#2e7d32' }}>{changes[0].value || '(cleared)'}</span>
            </Typography>
          ) : changes.length > 1 ? (
            <Typography variant="body2" color="text.secondary">
              {changes.length} fields changed
            </Typography>
          ) : '-'}
        </TableCell>
        <TableCell>{row.changedByUserName || row.changedByUserEmail || 'System'}</TableCell>
        <TableCell>
          <Chip 
            label={row.source} 
            color={getSourceColor(row.source)} 
            size="small"
            variant="outlined"
          />
        </TableCell>
      </TableRow>
      {hasMultipleChanges && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1, ml: 6 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>New Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {changes.map((change, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{formatFieldName(change.field)}</TableCell>
                        <TableCell sx={{ color: 'success.main' }}>{change.value || '(cleared)'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const ProfileAuditTab = ({ auth, siteId, accountId, profileId }) => {
  const [auditHistory, setAuditHistory] = useState([]);
  const [allAuditHistory, setAllAuditHistory] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: dayjs().subtract(30, 'day').startOf('day'),
    endDate: dayjs().endOf('day'),
    type: 'month'
  });

  useEffect(() => {
    if (allAuditHistory.length === 0) {
      setLoadingAudit(true);
      profileApi.getProfileAuditHistory(auth.user.access_token, siteId, accountId, profileId)
        .then(r => {
          setAllAuditHistory(r.data);
        })
        .catch(err => console.error('Error fetching audit history:', err))
        .finally(() => setLoadingAudit(false));
    }
  }, [auth.user.access_token, siteId, accountId, profileId, allAuditHistory.length]);

  useEffect(() => {
    // Filter locally when allAuditHistory or dateRange changes
    if (allAuditHistory.length > 0) {
      const filtered = allAuditHistory.filter(entry => {
        const entryDate = dayjs(entry.changedAt);
        return entryDate.isAfter(dateRange.startDate) && entryDate.isBefore(dateRange.endDate);
      });
      setAuditHistory(filtered);
    } else {
      setAuditHistory([]);
    }
  }, [allAuditHistory, dateRange]);

  const handleDateRangeChange = (type) => {
    let startDate = dayjs();
    let endDate = dayjs();

    switch (type) {
      case 'today':
        startDate = dayjs().startOf('day');
        endDate = dayjs().endOf('day');
        break;
      case 'week':
        startDate = dayjs().subtract(7, 'day').startOf('day');
        endDate = dayjs().endOf('day');
        break;
      case 'month':
        startDate = dayjs().subtract(30, 'day').startOf('day');
        endDate = dayjs().endOf('day');
        break;
      case 'all':
        startDate = dayjs('2000-01-01');
        endDate = dayjs().endOf('day');
        break;
      case 'custom':
        // Keep current dates for custom, just switch mode
        startDate = dateRange.startDate;
        endDate = dateRange.endDate;
        break;
      default:
        break;
    }
    setDateRange({ startDate, endDate, type });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Change History
      </Typography>
      
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <ButtonGroup variant="outlined" aria-label="date range button group">
          <Button 
            variant={dateRange.type === 'today' ? 'contained' : 'outlined'}
            onClick={() => handleDateRangeChange('today')}
          >
            Today
          </Button>
          <Button 
            variant={dateRange.type === 'week' ? 'contained' : 'outlined'}
            onClick={() => handleDateRangeChange('week')}
          >
            Last 7 Days
          </Button>
          <Button 
            variant={dateRange.type === 'month' ? 'contained' : 'outlined'}
            onClick={() => handleDateRangeChange('month')}
          >
            Last 30 Days
          </Button>
          <Button 
            variant={dateRange.type === 'all' ? 'contained' : 'outlined'}
            onClick={() => handleDateRangeChange('all')}
          >
            All Time
          </Button>
          <Button 
            variant={dateRange.type === 'custom' ? 'contained' : 'outlined'}
            onClick={() => handleDateRangeChange('custom')}
          >
            Custom
          </Button>
        </ButtonGroup>
        
        {dateRange.type === 'custom' && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <DatePicker
              label="Start Date"
              value={dateRange.startDate}
              onChange={(newValue) => setDateRange(prev => ({ ...prev, startDate: newValue }))}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="End Date"
              value={dateRange.endDate}
              onChange={(newValue) => setDateRange(prev => ({ ...prev, endDate: newValue }))}
              slotProps={{ textField: { size: 'small' } }}
            />
          </Box>
        )}
      </Box>

      {loadingAudit ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="audit history table">
            <TableHead>
              <TableRow>
                <TableCell width={50} />
                <TableCell>Date/Time</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Section</TableCell>
                <TableCell>Changes</TableCell>
                <TableCell>Changed By</TableCell>
                <TableCell>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auditHistory.map((row) => (
                <AuditRow key={row.id} row={row} />
              ))}
              {auditHistory.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No change history found for selected date range
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ProfileAuditTab;

